/* eslint-disable no-unused-vars */
import { useState, useMemo } from 'react';
import {
  CardElement,
  useStripe,
  useElements,
  // CardNumberElement,
  // CardExpiryElement,
  // CardCvcElement,
} from '@stripe/react-stripe-js';
import { paymentsAPI } from 'services/payments.service';
import { useSelector } from 'react-redux';
import { showPolipop } from 'utils';
import { Palette } from 'config';
import { Buttons, Message, IconButton } from 'components';
import { DeleteIconNew } from 'assets/icons';
import styled from 'styled-components';

export const StripeForm = ({ setIsEditMode, isHideCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const pb_email = useSelector((state) => state?.auth?.pb_email || '');
  const pb_username = useSelector((state) => state?.auth?.pb_username || '');

  const [isCardComplete, setIsCardComplete] = useState(false);

  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [addPaymentMethod, result] = paymentsAPI.useAddPaymentMethodMutation();

  const handleSubmit = async (event) => {
    setError('');
    event.preventDefault();
    // Check if Stripe.js has loaded and elements is available
    if (!stripe || !elements) return;
    // Get the CardElement instance
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      console.log('CardElement not found');
      return;
    }
    // Attempt to collect card details (without confirming the payment)
    try {
      setIsProcessing(true);
      const { token, error } = await stripe.createToken(cardElement);
      if (error) {
        setError(error.message);
        setIsProcessing(false);
        return;
      }
      // If you get the token, you can send it to your backend or save it
      const res = await addPaymentMethod({
        name: pb_username,
        email: pb_email,
        paymentMethodId: token.id,
      });
      if (res?.error) {
        showPolipop(res);
        setError('something went wrong');
      }
    } catch (err) {
      setError(err.message);
    }
    setIsProcessing(false);
  };

  const handleReset = (event) => {
    event.preventDefault();
    const cardElement = elements.getElement(CardElement);
    if (cardElement) {
      cardElement.clear();
    }
  };

  const btnIsDisabled = useMemo(() => {
    if (!stripe) return true;
    if (isProcessing) return true;
    return !isCardComplete;
  }, [isCardComplete, isProcessing, Boolean(stripe)]);

  return (
    <StyledForm onSubmit={handleSubmit}>
      <SmallText>Please enter your credit card details:</SmallText>
      <div>
        <div style={{ display: 'flex', width: '100%' }}>
          <StyledCardElement>
            <CardElement
              onChange={(event) => {
                if (!event.empty && !event.error) {
                  setError('');
                }
                if (!event.empty && event.error) {
                  setError(event.error.message);
                }
                setIsCardComplete(event.complete);
              }}
              options={{
                showIcon: true,
                disableLink: true,
                hidePostalCode: true,
                disabled: isProcessing,
                style: {
                  empty: {},
                  base: {
                    fontSize: '16px',
                    '::placeholder': {
                      color: '#94A3B8',
                    },
                    ':-webkit-autofill': {
                      color: '#f9c74f',
                    },
                  },
                  invalid: { color: 'red', iconColor: 'red' },
                  complete: {
                    color: 'green',
                  },
                },
              }}
            />
          </StyledCardElement>
          <IconButton.NormalSize
            icon={DeleteIconNew}
            disabled={false}
            type={IconButton.btnTypes.tertiary}
            onClick={handleReset}
            style={{}}
          />
        </div>
        <Message
          isOpen={Boolean(error)}
          type={Message.messageType.error}
          backgroundColor='transparent'
        >
          {error}
        </Message>
      </div>
      <Box>
        <div style={{ visibility: isHideCancel ? 'initial' : 'hidden' }}>
          <Buttons.SecondaryNormal onClick={() => setIsEditMode(true)}>
            Cancel
          </Buttons.SecondaryNormal>
        </div>
        <div style={{ marginRight: '35px' }}>
          <Buttons.PrimaryNormal isDisabled={btnIsDisabled} type='submit'>
            {isProcessing ? 'Processing...' : 'Connect'}
          </Buttons.PrimaryNormal>
        </div>
      </Box>
      <Text>Credit card processing fees will be included in every transaction.</Text>
    </StyledForm>
  );
};
const Box = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Text = styled.p`
  color: ${Palette.PrimaryGray._700};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const StyledForm = styled.form`
  all: unset;
  width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0px 0px 0px;
  height: 210px;
`;

const StyledCardElement = styled.div`
  padding: 10px;
  border: 1px solid ${Palette.PrimaryGray._200};
  background: ${Palette.PrimaryGray._000};
  width: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;

  .StripeElement {
    width: 100%;
  }
`;

const SmallText = styled.p`
  font-size: 12px;
  line-height: 16px;
  color: ${Palette.PrimaryGray._700};
`;
